import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthGuard } from "./auth/AuthGuard";
import { CallbackPage } from "./auth/CallbackPage";
import { LoggedOutPage } from "./auth/LoggedOutPage";
import { RoleGuard } from "./auth/RoleGuard";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AppShell } from "./components/shell/AppShell";
import { RecentGroupsPage } from "./pages/RecentGroupsPage";
import { SearchGroupsPage } from "./pages/SearchGroupsPage";
import { AddGroupPage } from "./pages/AddGroupPage";
import { GroupOverviewPage } from "./pages/GroupOverviewPage";
import { EditGroupPage } from "./pages/EditGroupPage";
import { SentCensusPage } from "./pages/SentCensusPage";
import { MemberFormPage } from "./pages/MemberFormPage";
import { MemberOverviewPage } from "./pages/MemberOverviewPage";
import { SelectConditionPage } from "./pages/SelectConditionPage";
import { SelectRatingPage } from "./pages/SelectRatingPage";
import { ExpectedDebitTablesPage } from "./pages/ExpectedDebitTablesPage";
import { NewExpectedDebitsTablePage } from "./pages/NewExpectedDebitsTablePage";
import { CustomizeConditionsPage } from "./pages/CustomizeConditionsPage";
import { NewCustomizationPage } from "./pages/NewCustomizationPage";
import { ModifyConditionPage } from "./pages/ModifyConditionPage";
import { ConditionEditorPage } from "./pages/ConditionEditorPage";
import { MugsManualUploadPage } from "./pages/MugsManualUploadPage";
import { ForbiddenPage } from "./pages/ForbiddenPage";

/** Route table under /uw. Every screen is usable at its own URL. */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/uw/recent" replace />} />
      {/* The OIDC callback and logged-out pages must stay outside the AuthGuard: the callback is where the code is
          exchanged for tokens, so no one is signed in yet when it renders. Same shape as the employer app. */}
      <Route path="/auth/callback" element={<CallbackPage />} />
      <Route path="/logged-out" element={<LoggedOutPage />} />
      <Route
        path="/uw"
        element={
          <AuthGuard>
            <AppShell />
          </AuthGuard>
        }
      >
        <Route index element={<Navigate to="/uw/recent" replace />} />
        <Route path="recent" element={<RecentGroupsPage />} />
        <Route path="search" element={<SearchGroupsPage />} />
        <Route path="groups/new" element={<AddGroupPage />} />
        <Route path="groups/:groupId" element={<GroupOverviewPage />} />
        <Route path="groups/:groupId/edit" element={<EditGroupPage />} />
        <Route path="groups/:groupId/sent-census" element={<SentCensusPage />} />
        <Route path="groups/:groupId/members/new" element={<MemberFormPage mode="add" />} />
        <Route path="groups/:groupId/members/:memberId" element={<MemberOverviewPage />} />
        <Route path="groups/:groupId/members/:memberId/edit" element={<MemberFormPage mode="edit" />} />
        <Route path="groups/:groupId/members/:memberId/conditions/select" element={<SelectConditionPage />} />
        <Route path="groups/:groupId/members/:memberId/conditions/:conditionId/ratings" element={<SelectRatingPage />} />
        <Route
          path="actuarial/expected-debits"
          element={
            <RoleGuard requires="Actuarial">
              <ExpectedDebitTablesPage />
            </RoleGuard>
          }
        />
        <Route
          path="actuarial/expected-debits/new"
          element={
            <RoleGuard requires="Actuarial">
              <NewExpectedDebitsTablePage />
            </RoleGuard>
          }
        />
        <Route
          path="actuarial/conditions"
          element={
            <RoleGuard requires="Actuarial">
              <CustomizeConditionsPage />
            </RoleGuard>
          }
        />
        <Route
          path="actuarial/conditions/new"
          element={
            <RoleGuard requires="Actuarial">
              <NewCustomizationPage />
            </RoleGuard>
          }
        />
        <Route
          path="actuarial/conditions/modify"
          element={
            <RoleGuard requires="Actuarial">
              <ModifyConditionPage />
            </RoleGuard>
          }
        />
        <Route
          path="actuarial/conditions/edit"
          element={
            <RoleGuard requires="Actuarial">
              <ConditionEditorPage />
            </RoleGuard>
          }
        />
        <Route path="actuarial/debits" element={<Navigate to="/uw/actuarial/conditions/modify" replace />} />
        <Route
          path="actuarial/mugs-manual-upload"
          element={
            <RoleGuard requires="Actuarial">
              <MugsManualUploadPage />
            </RoleGuard>
          }
        />
        <Route path="forbidden" element={<ForbiddenPage />} />
        <Route path="*" element={<Navigate to="/uw/recent" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/uw/recent" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
